import { Button, Checkbox, Popover } from '@ntconsult/aluma-react';
import { Columns3 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnsState } from '../hooks/createColumnsHook';

type ColumnsPickerProps<C extends string> = {
  columnsState: ColumnsState<C>;
  /** Chaves i18n do `namespace`. */
  labelKeys: Record<C, string>;
  namespace?: 'alocacao' | 'tagsAlocacao' | 'projetos';
};

/** Como os filtros de coluna, só comita no "Aplicar"; fechar de outro jeito descarta o rascunho. */
export function ColumnsPicker<C extends string>({
  columnsState,
  labelKeys,
  namespace = 'alocacao',
}: ColumnsPickerProps<C>) {
  const { t } = useTranslation(['alocacao', 'common']);
  const { columns, defaultColumns, visibleColumns, setVisibleColumns } = columnsState;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<C[]>(visibleColumns);

  function handleOpenChange(next: boolean) {
    if (next) setDraft(visibleColumns);
    setOpen(next);
  }

  function toggle(column: C, checked: boolean) {
    setDraft((current) =>
      checked ? [...current, column] : current.filter((item) => item !== column),
    );
  }

  function apply(next: readonly C[]) {
    setVisibleColumns([...next]);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottom-end"
      trigger={
        <Button variant="secondary" size="sm" leftIcon={<Columns3 size={14} />}>
          {t('alocacao:colunas.titulo')}
        </Button>
      }
    >
      <form
        className="flex w-56 flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (draft.length > 0) apply(draft);
        }}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-foreground">
            {t('alocacao:colunas.visiveis')}
          </legend>
          {columns.map((column) => (
            <Checkbox
              key={column}
              label={t(`${namespace}:${labelKeys[column]}` as never)}
              checked={draft.includes(column)}
              onCheckedChange={(checked) => toggle(column, checked)}
            />
          ))}
        </fieldset>
        {draft.length === 0 && (
          <p className="text-sm text-foreground-muted" role="status">
            {t('alocacao:colunas.nenhumaSelecionada')}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => apply(defaultColumns)}>
            {t('alocacao:colunas.redefinir')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={draft.length === 0}>
            {t('alocacao:colunas.aplicar')}
          </Button>
        </div>
      </form>
    </Popover>
  );
}
